import { s } from '@fullcalendar/core/internal-common'
import {
  Box,
  CardContentProps,
  ClickAwayListener,
  Grid,
  Grid2,
  Typography
} from '@mui/material'
import { StepType, TourProps, TourProvider, useTour } from '@reactour/tour'
import Button from 'components/buttons/Button'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { t } from 'i18next'
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState
} from 'react'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp } from 'utils/appType'
import { getUserABGroup } from 'utils/onboarding'

export interface TFMTourProviderProps {
  children: React.ReactNode
  onClickMask?: () => void
  mode: ONBOARDING_MODE
}

export interface SingleTutor {
  id: string
  title: string
  message: string
}

interface TFMTourProviderContextProps {
  mode: ONBOARDING_MODE | undefined
  setMode: (mode: ONBOARDING_MODE) => void
  setSingleTutorial: (tutorial: SingleTutor) => void
  showStepIfNeeded: (step: string, tourProps: TourProps) => boolean
  getStepsByMode?: (mode: ONBOARDING_MODE) => StepType[]
}

const TFMTourProviderContext = createContext<TFMTourProviderContextProps>({
  mode: undefined,
  setMode: (mode: ONBOARDING_MODE) => {},
  setSingleTutorial: (tutorial: SingleTutor) => {},
  showStepIfNeeded: (step: string, tourProps: TourProps) => false,
  getStepsByMode: (mode: ONBOARDING_MODE) => []
})

export enum ONBOARDING_MODE {
  FIRST_LOGIN = 'FIRST_LOGIN',
  DIMMED = 'DIMMED',
  FROM_GROUP = 'FROM_GROUP',
  FROM_ALL = 'FROM_ALL',
  DUPLICATED = 'DUPLICATED'
}

export default function TFMTourProvider(props: TFMTourProviderProps) {
  const [mode, setMode] = useState<ONBOARDING_MODE>(props.mode)

  const [singleTutorDialog, setSingleTutorialDialog] = useState<
    SingleTutor | undefined
  >(undefined)

  const setSingleTutorial = (tutorial: SingleTutor) => {
    if (checkCompletedStep(tutorial.id)) {
      return
    }

    LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
    setSingleTutorialDialog(tutorial)
  }

  function showStepIfNeeded(step: string, tourProps: TourProps): boolean {
    const state = checkCompletedStep(step)

    if (!state) {
      tourProps.setCurrentStep(tourProps.currentStep + 1)
      tourProps.setIsOpen(true)
    }

    return state
  }

  const [context, setContext] = useState<TFMTourProviderContextProps>({
    mode,
    setMode,
    setSingleTutorial,
    showStepIfNeeded: showStepIfNeeded,
    getStepsByMode: getStepsByMode
  })

  Logger.blue('TFMTourProvider', { mode })

  return (
    <TFMTourProviderContext.Provider value={context}>
      <TourProvider
        styles={{
          popover: (base) => ({
            ...base,
            '--reactour-accent': '#ef5a3d',
            borderRadius: 16,
            left: '8px',
            padding: '30px 30px 0px 30px'
          }),
          maskArea: (base) => ({
            ...base,
            rx: 18 // Rounded corners
          })
        }}
        scrollSmooth
        steps={getStepsByMode(mode)}
        showBadge={false}
        showCloseButton={false}
        showPrevNextButtons={false}
        showDots={false}
        disableKeyboardNavigation
        disableDotsNavigation
        onClickMask={({ setCurrentStep, currentStep, steps, setIsOpen }) => {
          if (props.onClickMask) {
            props.onClickMask()
          }

          if (currentStep === (steps?.length ?? 0) - 1) {
            setIsOpen(false)
            return
          } else {
            setCurrentStep(currentStep + 1)
          }
        }}
      >
        {props.children}
        {singleTutorDialog && (
          <ConfirmationDialog
            title={singleTutorDialog.title}
            content={singleTutorDialog.message}
            open={true}
            handleClose={() => {
              completeStep(singleTutorDialog.id)
            }}
            leftButtonHidden
            handleRightButton={() => {
              setSingleTutorialDialog(undefined)
              completeStep(singleTutorDialog.id)
            }}
          />
        )}
        <Content />
      </TourProvider>
    </TFMTourProviderContext.Provider>
  )
}

function Content() {
  const tour = useTour()
  const { isOpen, currentStep, steps, setCurrentStep, setIsOpen } = tour

  const props: OnboardingContentProps = (
    steps[currentStep]?.content as ReactElement
  )?.props

  return props?.blockUserInteraction === true &&
    isOpen &&
    currentStep != steps.length - 1 ? (
    <Box
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        e.nativeEvent.stopImmediatePropagation()

        if (!props?.doNotMoveStep) {
          if (currentStep === steps.length - 1) {
            setIsOpen(false)
            return
          }

          setCurrentStep((s) => s + 1)
        }
      }}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        backgroundColor: 'transparent' // Semi-transparent background
      }}
    />
  ) : (
    <></>
  )
}

export interface OnboardingContentProps {
  title: string
  subtitle: string
  doNotMoveStep?: boolean
  conditionalStep?: (props: TourProps) => void
  blockUserInteraction?: boolean
  nextButtonAction?: (props: TourProps) => void
}

export function OnboardingContent(props: OnboardingContentProps) {
  const tour = useTour()
  const { currentStep, steps, setCurrentStep, setIsOpen } = tour

  const performConditionalLogic = () => {
    setIsOpen(false)

    if (props.conditionalStep) {
      props.conditionalStep(tour)
    }
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        // do nothing
        if (!props.blockUserInteraction) {
          if (props.conditionalStep) {
            performConditionalLogic()
            return
          }
        }
      }}
    >
      <Grid container direction={'column'} spacing={2}>
        <Typography
          fontWeight={600}
          fontSize={'1.1rem'}
          paddingBottom={'10px'}
          paddingTop={'5px'}
        >
          {props.title}
        </Typography>
        <Typography fontSize={'15px'}>{props.subtitle}</Typography>
        <Grid2
          paddingTop={'16px'}
          direction={'row'}
          container
          justifyContent={'space-between'}
        >
          <Typography
            sx={{
              textDecoration: 'underline',
              color: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer'
            }}
            onClick={() => {
              setIsOpen(false)
              LocalStorage.set(LocalStorageKey.USER_FINSHED_ONBOARDING, '1')
            }}
            fontSize={'15px'}
          >
            {t('quiteTutorial')}
          </Typography>
          <Button
            onClick={() => {
              if (props.nextButtonAction) {
                props.nextButtonAction(tour)
              }

              if (props.doNotMoveStep) {
                performConditionalLogic()
                return
              }

              if (currentStep === steps.length - 1) {
                setIsOpen(false)
                return
              }

              setCurrentStep((s) => s + 1)
            }}
            size="xs"
          >
            {props.nextButtonAction
              ? t('doIt')
              : props.doNotMoveStep || currentStep === steps.length - 1
              ? t('continue')
              : t('nextTip')}
          </Button>
        </Grid2>
      </Grid>
    </ClickAwayListener>
  )
}

function getStepsByMode(mode: ONBOARDING_MODE) {
  const onboardMainMessage: StepType = {
    selector: '#somewhere',
    position: 'center',
    styles: {
      maskArea: (base) => ({
        ...base,
        rx: 0, // Rounded corners
        padding: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none'
      })
    },
    content: (
      <OnboardingContent
        title={t('onboardMainMessageTitle')}
        subtitle={t('onboardMainMessage', { appName: t('appName') })}
      />
    ),
    actionAfter: () => {
      completeStep('#somewhere')
    }
  }

  const onboardGroupSectionMessage: StepType = {
    selector: '#first-group',
    content: (
      <OnboardingContent
        title={t('onboardGroupClickTitle')}
        subtitle={t('onboardOnGroupClick')}
        blockUserInteraction
      />
    ),
    actionAfter: () => {
      completeStep('#first-group')
    }
  }

  const onboardSharedCalendarScreen: StepType = {
    selector: '#calendar-root',
    position: 'center',
    content: (
      <OnboardingContent
        title={t('onboardGroupDetailsTitle')}
        subtitle={t('onboardGroupDetails')}
        blockUserInteraction
      />
    ),
    actionAfter: () => {
      completeStep('#calendar-root')
    }
  }

  const onboardSharedCalendarInfo: StepType = {
    selector: '#calendar-info-calendar',
    content: (
      <OnboardingContent
        title={t('onboardTapGroupInfoTitle')}
        subtitle={t('onboardTapGroupInfo')}
        nextButtonAction={() => {
          clickElementById('calendar-info-calendar')
        }}
        doNotMoveStep={
          LocalStorage.get(LocalStorageKey.SELECTED_GROUP)?.length !== 0
        }
        conditionalStep={(props) => {
          setTimeout(() => {
            if (
              LocalStorage.get(LocalStorageKey.SELECTED_GROUP)?.length !== 0 &&
              !LocalStorage.get(LocalStorageKey.SHOWING_GROUP_INFO)
            ) {
              props.setIsOpen(true)
            }
          }, 250)
        }}
      />
    ),
    actionAfter: () => {
      completeStep('#calendar-info-calendar')
    }
  }

  const onboardAddSharedCalendar: StepType = {
    selector: '#add-group-button',
    content: (
      <OnboardingContent
        title={''}
        subtitle={t('onboardAddSharedCalendar')}
        nextButtonAction={(tour) => {
          clickElementById('add-group-button')
          if (tour.currentStep === tour.steps.length - 1) {
            tour.setIsOpen(false)
          }
        }}
      />
    ),
    actionAfter: () => {
      completeStep('#add-group-button')
    }
  }

  const dimmedEventsMessage: StepType = {
    selector: '#somewhere_dimmed',
    position: 'center',
    styles: {
      maskArea: (base) => ({
        ...base,
        rx: 0, // Rounded corners
        padding: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none'
      })
    },
    content: (
      <OnboardingContent
        title={t('onboardDimmedEventsTitle')}
        subtitle={t('onboardDimmedEvents')}
      />
    ),
    actionAfter: () => {
      completeStep('#somewhere_dimmed')
    }
  }

  const duplicatedEventsMessage: StepType = {
    selector: '#somewhere_dimmed',
    position: 'center',
    styles: {
      maskArea: (base) => ({
        ...base,
        rx: 0, // Rounded corners
        padding: 0,
        width: 0,
        height: 0,
        pointerEvents: 'none'
      })
    },
    content: (
      <OnboardingContent
        title={t('duplicate')}
        subtitle={t('onboardDuplicateItem')}
      />
    ),
    actionAfter: () => {
      completeStep('#duplicated_dimmed')
    }
  }

  let steps: StepType[] = []

  let ONBOARDING_STEPS_A = [
    onboardMainMessage,
    onboardGroupSectionMessage,
    onboardSharedCalendarScreen,
    onboardSharedCalendarInfo,
    onboardAddSharedCalendar
  ]

  if (LocalStorage.get(LocalStorageKey.SELECTED_GROUP)?.length === 0) {
    ONBOARDING_STEPS_A = [
      onboardMainMessage,
      onboardGroupSectionMessage,
      onboardSharedCalendarScreen,
      onboardAddSharedCalendar
    ]
  }
  const ONBOARDING_STEPS_B = [onboardMainMessage]

  switch (mode) {
    case ONBOARDING_MODE.FIRST_LOGIN:
      steps = getUserABGroup() === 'A' ? ONBOARDING_STEPS_A : ONBOARDING_STEPS_B
      break
    case ONBOARDING_MODE.DIMMED:
      steps = [dimmedEventsMessage]
      break
    case ONBOARDING_MODE.DUPLICATED:
      steps = [duplicatedEventsMessage]
      break
    case ONBOARDING_MODE.FROM_GROUP:
      steps = [
        onboardSharedCalendarScreen,
        onboardSharedCalendarInfo,
        onboardAddSharedCalendar
      ]
      break
    case ONBOARDING_MODE.FROM_ALL:
      steps = [onboardSharedCalendarScreen, onboardAddSharedCalendar]
      break
    default:
      steps = []
  }

  if (
    is24meApp() ||
    LocalStorage.get(LocalStorageKey.USER_FINSHED_ONBOARDING) === '1'
  )
    return []

  const filtered = steps.filter((step) => {
    const state = JSON.parse(
      LocalStorage.get(LocalStorageKey.STATE_DICT) || '{}'
    )

    return !(step.selector.toString() in state)
  })

  if (filtered.length === 0) {
    LocalStorage.set(LocalStorageKey.USER_FINSHED_ONBOARDING, '1')
  }

  Logger.blue('TFMTourProvider', { filtered })

  return filtered
}

function checkCompletedStep(step: string): boolean {
  const state = JSON.parse(LocalStorage.get(LocalStorageKey.STATE_DICT) || '{}')

  return state[step] === true
}

function completeStep(step: string) {
  const existing = JSON.parse(
    LocalStorage.get(LocalStorageKey.STATE_DICT) || '{}'
  )

  LocalStorage.set(
    LocalStorageKey.STATE_DICT,
    JSON.stringify({ ...existing, [step]: true })
  )
}

function clickElementById(elementId: string): void {
  const element = document.getElementById(elementId)
  if (element) {
    // Click the parent element
    element.click()
  } else {
    console.warn(`Element with ID ${elementId} not found.`)
  }
}

function clickElementsByClassName(className: string): void {
  const elements = document.getElementsByClassName(className)
  if (elements.length > 0 && elements[0] instanceof HTMLElement) {
    elements[0].click()
  } else {
    console.warn(
      `No elements with class name ${className} found or the first element is not an HTMLElement.`
    )
  }
}

export function useTfmTourProvider(): TFMTourProviderContextProps {
  return useContext(TFMTourProviderContext)
}
